import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Divider,
  Slider,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as BuyIcon,
  TrendingDown as SellIcon,
  SwapHoriz as SwapIcon,
  AccountBalance as BalanceIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface Market {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  volume24h: number;
  minOrderSize: number;
  maxOrderSize: number;
  priceStep: number;
  quantityStep: number;
}

interface OrderFormProps {
  market: Market;
  balance: {
    base: number;
    quote: number;
  };
  onSubmitOrder: (order: {
    side: 'buy' | 'sell';
    type: 'market' | 'limit' | 'stop-limit';
    quantity: number;
    price?: number;
    stopPrice?: number;
    timeInForce: 'GTC' | 'IOC' | 'FOK';
  }) => void;
  loading?: boolean;
}

const OrderForm: React.FC<OrderFormProps> = ({
  market,
  balance,
  onSubmitOrder,
  loading = false,
}) => {
  const theme = useTheme();
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState<'market' | 'limit' | 'stop-limit'>('limit');
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>(market.price.toString());
  const [stopPrice, setStopPrice] = useState<string>('');
  const [timeInForce, setTimeInForce] = useState<'GTC' | 'IOC' | 'FOK'>('GTC');
  const [percentageSlider, setPercentageSlider] = useState<number>(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const parts = market.symbol.split('-');
  const baseCurrency = parts[0] || market.symbol;
  const quoteCurrency = parts[1] || 'EUR';

  useEffect(() => {
    if (orderType === 'market') {
      setPrice('');
    } else if (orderType === 'limit' && !price) {
      setPrice(market.price.toString());
    }
  }, [orderType, market.price]);

  useEffect(() => {
    validateForm();
  }, [quantity, price, stopPrice, side, orderType, market]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    const qty = parseFloat(quantity);
    const prc = parseFloat(price);
    const stopPrc = parseFloat(stopPrice);

    // Quantity validation
    if (!quantity || qty <= 0) {
      newErrors.quantity = 'Menge muss größer als 0 sein';
    } else if (qty < market.minOrderSize) {
      newErrors.quantity = `Mindestmenge: ${market.minOrderSize} ${baseCurrency}`;
    } else if (qty > market.maxOrderSize) {
      newErrors.quantity = `Maximalmenge: ${market.maxOrderSize} ${baseCurrency}`;
    }

    // Price validation for limit orders
    if (orderType !== 'market') {
      if (!price || prc <= 0) {
        newErrors.price = 'Preis muss größer als 0 sein';
      }
    }

    // Stop price validation for stop-limit orders
    if (orderType === 'stop-limit') {
      if (!stopPrice || stopPrc <= 0) {
        newErrors.stopPrice = 'Stop-Preis muss größer als 0 sein';
      } else if (side === 'buy' && stopPrc <= market.price) {
        newErrors.stopPrice = 'Stop-Preis muss über dem aktuellen Preis liegen';
      } else if (side === 'sell' && stopPrc >= market.price) {
        newErrors.stopPrice = 'Stop-Preis muss unter dem aktuellen Preis liegen';
      }
    }

    // Balance validation
    if (side === 'buy') {
      const requiredQuote = qty * (orderType === 'market' ? market.price : prc);
      if (requiredQuote > balance.quote) {
        newErrors.balance = `Unzureichendes ${quoteCurrency} Guthaben`;
      }
    } else {
      if (qty > balance.base) {
        newErrors.balance = `Unzureichendes ${baseCurrency} Guthaben`;
      }
    }

    setErrors(newErrors);
  };

  const calculateTotal = () => {
    const qty = parseFloat(quantity) || 0;
    const prc = orderType === 'market' ? market.price : parseFloat(price) || 0;
    return qty * prc;
  };

  const calculateMaxQuantity = () => {
    if (side === 'buy') {
      const prc = orderType === 'market' ? market.price : parseFloat(price) || market.price;
      return Math.min(balance.quote / prc, market.maxOrderSize);
    } else {
      return Math.min(balance.base, market.maxOrderSize);
    }
  };

  const handlePercentageChange = (percentage: number) => {
    setPercentageSlider(percentage);
    const maxQty = calculateMaxQuantity();
    const newQuantity = (maxQty * percentage / 100).toFixed(8);
    setQuantity(newQuantity);
  };

  const handleSubmit = () => {
    if (Object.keys(errors).length === 0) {
      onSubmitOrder({
        side,
        type: orderType,
        quantity: parseFloat(quantity),
        price: orderType !== 'market' ? parseFloat(price) : undefined,
        stopPrice: orderType === 'stop-limit' ? parseFloat(stopPrice) : undefined,
        timeInForce,
      });
    }
  };

  const getSideColor = () => {
    return side === 'buy' ? theme.palette.success.main : theme.palette.error.main;
  };

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value).replace('€', currency);
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Order erstellen
          </Typography>
          <Chip
            label={market.symbol}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Market Info */}
        <Box
          p={2}
          borderRadius={1}
          mb={3}
          sx={{
            backgroundColor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Aktueller Preis
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatCurrency(market.price, quoteCurrency)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="body2" color="text.secondary">
              24h Änderung
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: market.change24h >= 0 ? theme.palette.success.main : theme.palette.error.main,
              }}
            >
              {market.change24h >= 0 ? '+' : ''}{market.change24h.toFixed(2)}%
            </Typography>
          </Box>
        </Box>

        {/* Buy/Sell Toggle */}
        <ToggleButtonGroup
          value={side}
          exclusive
          onChange={(_, value) => value && setSide(value)}
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton
            value="buy"
            sx={{
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                borderColor: theme.palette.success.main,
              },
            }}
          >
            <BuyIcon sx={{ mr: 1 }} />
            Kaufen
          </ToggleButton>
          <ToggleButton
            value="sell"
            sx={{
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                borderColor: theme.palette.error.main,
              },
            }}
          >
            <SellIcon sx={{ mr: 1 }} />
            Verkaufen
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Order Type */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Order-Typ</InputLabel>
          <Select
            value={orderType}
            label="Order-Typ"
            onChange={(e) => setOrderType(e.target.value as 'market' | 'limit' | 'stop-limit')}
          >
            <MenuItem value="market">Market Order</MenuItem>
            <MenuItem value="limit">Limit Order</MenuItem>
            <MenuItem value="stop-limit">Stop-Limit Order</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {orderType === 'market' && 'Sofort zum aktuellen Marktpreis kaufen/verkaufen. Schnell, aber der Preis kann leicht abweichen.'}
            {orderType === 'limit' && 'Nur zum gewünschten Preis oder besser kaufen/verkaufen. Du bestimmst den Preis.'}
            {orderType === 'stop-limit' && 'Wird erst aktiv wenn der Stop-Preis erreicht wird, dann als Limit-Order ausgeführt.'}
          </Typography>
        </FormControl>

        {/* Price Input (nur für Limit Orders) */}
        {orderType !== 'market' && (
          <TextField
            fullWidth
            label="Preis"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            error={!!errors.price}
            helperText={errors.price}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {quoteCurrency}
                  </Typography>
                  <Tooltip title="Aktuellen Marktpreis verwenden">
                    <IconButton
                      size="small"
                      onClick={() => setPrice(market.price.toString())}
                    >
                      <SwapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        )}

        {/* Stop Price (nur für Stop-Limit Orders) */}
        {orderType === 'stop-limit' && (
          <TextField
            fullWidth
            label="Stop-Preis"
            value={stopPrice}
            onChange={(e) => setStopPrice(e.target.value)}
            error={!!errors.stopPrice}
            helperText={errors.stopPrice}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {quoteCurrency}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        )}

        {/* Quantity Input */}
        <TextField
          fullWidth
          label="Menge"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          error={!!errors.quantity}
          helperText={errors.quantity}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">
                  {baseCurrency}
                </Typography>
                <Tooltip title="Maximale Menge berechnen">
                  <IconButton
                    size="small"
                    onClick={() => setQuantity(calculateMaxQuantity().toFixed(8))}
                  >
                    <CalculateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Percentage Slider */}
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Prozent des verfügbaren Guthabens
          </Typography>
          <Slider
            value={percentageSlider}
            onChange={(_, value) => handlePercentageChange(value as number)}
            marks={[
              { value: 0, label: '0%' },
              { value: 25, label: '25%' },
              { value: 50, label: '50%' },
              { value: 75, label: '75%' },
              { value: 100, label: '100%' },
            ]}
            step={5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
            sx={{
              color: getSideColor(),
              '& .MuiSlider-mark': {
                backgroundColor: getSideColor(),
              },
              '& .MuiSlider-markLabel': {
                fontSize: '0.7rem',
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Summary */}
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Order Zusammenfassung
          </Typography>

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Gesamt
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(calculateTotal(), quoteCurrency)}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Tooltip title="Bitvavo berechnet 0,25% Handelsgebühr pro Trade" arrow>
              <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }}>
                <Typography variant="body2" color="text.secondary">
                  Geschätzte Gebühr (0.25%)
                </Typography>
                <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
            </Tooltip>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(calculateTotal() * 0.0025, quoteCurrency)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Gesamtkosten
            </Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {formatCurrency(calculateTotal() * 1.0025, quoteCurrency)}
            </Typography>
          </Box>
        </Box>

        {/* Balance Info */}
        <Box
          p={2}
          borderRadius={1}
          mb={3}
          sx={{
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <BalanceIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Verfügbares Guthaben
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">
              {formatCurrency(balance.base, baseCurrency)}
            </Typography>
            <Typography variant="body2">
              {formatCurrency(balance.quote, quoteCurrency)}
            </Typography>
          </Box>
        </Box>

        {/* Time in Force (für Limit Orders) */}
        {orderType !== 'market' && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Gültigkeitsdauer</InputLabel>
            <Select
              value={timeInForce}
              label="Gültigkeitsdauer"
              onChange={(e) => setTimeInForce(e.target.value as any)}
            >
              <MenuItem value="GTC">GTC — Gültig bis Stornierung</MenuItem>
              <MenuItem value="IOC">IOC — Sofort oder Stornierung</MenuItem>
              <MenuItem value="FOK">FOK — Alles oder Nichts</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Error Messages */}
        {errors.balance && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.balance}
          </Alert>
        )}

        {orderType === 'market' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Market Orders werden zum besten verfügbaren Preis ausgeführt.
              Der finale Preis kann vom angezeigten Preis abweichen.
            </Typography>
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading || Object.keys(errors).length > 0 || !quantity}
          sx={{
            backgroundColor: getSideColor(),
            '&:hover': {
              backgroundColor: alpha(getSideColor(), 0.8),
            },
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          {loading ? 'Order wird erstellt...' : `${side === 'buy' ? 'Kaufen' : 'Verkaufen'} ${baseCurrency}`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default OrderForm;